import { API_BASE_URL } from "../config/api";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import EngineerLayout from "../components/EngineerLayout";
import { buildComplaintIdPreview } from "../utils/reportIdentifiers";
import { formatPhoneDisplay } from "../utils/phone";
import { getWorkAddress } from "../utils/customerForm";

function CreateComplaint() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const role = localStorage.getItem("role") || "";
  const isEngineerMode = location.pathname.startsWith("/engineer");
  const isAccountsMode = location.pathname.startsWith("/accounts");
  const serviceId = String(searchParams.get("serviceId") || "").trim();
  const queryType = String(searchParams.get("type") || "").trim().toLowerCase();
  const isServiceEditMode = !id && Boolean(serviceId);
  const isEditMode = Boolean(id) || isServiceEditMode;
  const canEditComplaint = role === "admin" || role === "accounts";
  const engineerName = localStorage.getItem("engineerName") || "";
  const engineerLookup =
    localStorage.getItem("engineerId") ||
    localStorage.getItem("engineerEmail") ||
    engineerName;
  const revisitPrefill = location.state?.prefill || null;
  const initialEngineerAssignedRef = useRef(revisitPrefill?.engineerAssigned || "");

  const [customers, setCustomers] = useState([]);
  const [engineers, setEngineers] = useState([]);
  
  const [selectedCustomer, setSelectedCustomer] = useState(() => {
    if (revisitPrefill) {
      return {
        value: revisitPrefill.customerId,
        label: revisitPrefill.company,
        data: {
          _id: revisitPrefill.customerId,
          name: revisitPrefill.company,
          contactPerson: revisitPrefill.contactName,
          phone: revisitPrefill.contactPhone,
          address: revisitPrefill.address
        }
      };
    }
    return null;
  });
  
  const [selectedEngineer, setSelectedEngineer] = useState(null);
  const [visitDateInput, setVisitDateInput] = useState("");
  const [loadingComplaint, setLoadingComplaint] = useState(false);
  const [saving, setSaving] = useState(false);
  const submitLockRef = useRef(false);

  // Compute today / due-date defaults for service mode
  const initIsAbb = queryType === "abb_warranty" || queryType === "abb" || queryType === "abbwarranty" || Boolean(location.state?.isAbbWarranty);
  const initIsService = !isEngineerMode && queryType === "service";
  const defaultType = initIsAbb ? "abb_warranty" : (isEngineerMode ? "complaint" : queryType === "service" ? "service" : "complaint");
  const _today = new Date();
  const _ty = _today.getFullYear();
  const _tm = String(_today.getMonth() + 1).padStart(2, "0");
  const _td = String(_today.getDate()).padStart(2, "0");
  const _due = new Date(_today);
  _due.setFullYear(_due.getFullYear() + 1);
  _due.setDate(_due.getDate() - 1);
  const _dy = _due.getFullYear();
  const _dm = String(_due.getMonth() + 1).padStart(2, "0");
  const _dd = String(_due.getDate()).padStart(2, "0");

  const [serviceDateInput, setServiceDateInput] = useState(initIsService ? `${_ty}-${_tm}-${_td}` : "");
  const [nextServiceDateInput, setNextServiceDateInput] = useState(initIsService ? `${_dy}-${_dm}-${_dd}` : "");
  const [complaintCount, setComplaintCount] = useState(0);

  const [form, setForm] = useState(() => {
    if (revisitPrefill) {
      const isAbb = revisitPrefill.isAbbWarranty || revisitPrefill.productType === "ABB Warranty";
      return {
        type: isAbb ? "abb_warranty" : "complaint",
        isAbbWarranty: isAbb,
        complaintId: revisitPrefill.complaintId || "Will be generated on save",
        serviceId: "Will be generated on save",
        customerId: revisitPrefill.customerId || (isAbb ? "ABB-WARRANTY" : ""),
        productType: revisitPrefill.productType || (isAbb ? "ABB Warranty" : ""),
        nature: revisitPrefill.nature || revisitPrefill.issue || "",
        siteAdvice: "",
        sharePreviousHistory: Boolean(revisitPrefill.sharePreviousHistory),
        engineerAssigned: "",
        visitDate: "",
        serviceDate: "",
        nextServiceDate: "",
        stickerRequired: false,
        assignedEngineers: [],
        company: revisitPrefill.company || "",
        issue: revisitPrefill.issue || revisitPrefill.nature || "",
        contactName: revisitPrefill.contactName || "",
        contactPhone: revisitPrefill.contactPhone || "",
        contactEmail: revisitPrefill.contactEmail || "",
        address: revisitPrefill.address || "",
        revisitOf: revisitPrefill.revisitOf || "",
        originalComplaintId: revisitPrefill.originalComplaintId || ""
      };
    }
    return {
      type: defaultType,
      isAbbWarranty: initIsAbb,
      abbTicketNo: revisitPrefill?.abbTicketNo || "",
      complaintId: "Will be generated on save",
      serviceId: "Will be generated on save",
      customerId: initIsAbb ? "ABB-WARRANTY" : "",
      productType: initIsAbb ? "ABB Warranty" : "",
      nature: "",
      siteAdvice: "",
      sharePreviousHistory: false,
      engineerAssigned: "",
      visitDate: "",
      serviceDate: initIsService ? `${_td}/${_tm}/${_ty}` : "",
      nextServiceDate: initIsService ? `${_dd}/${_dm}/${_dy}` : "",
      stickerRequired: initIsService,
      assignedEngineers: [],
      company: "",
      issue: "",
      contactName: "",
      contactPhone: "",
      contactEmail: "",
      address: "",
      revisitOf: "",
      originalComplaintId: ""
    };
  });

  const [dynamicTeams, setDynamicTeams] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    axios.get(API_BASE_URL + "/api/admin-settings/system-settings", { headers })
      .then(res => {
        if (res.data && Array.isArray(res.data.teams)) {
          setDynamicTeams(res.data.teams);
        } else {
          setDynamicTeams([
            { id: "team_1", name: "Team 1" },
            { id: "team_2", name: "Team 2" },
            { id: "team_3", name: "Team 3" }
          ]);
        }
      })
      .catch(err => {
        console.error("Error loading system settings teams:", err);
        setDynamicTeams([
          { id: "team_1", name: "Team 1" },
          { id: "team_2", name: "Team 2" },
          { id: "team_3", name: "Team 3" }
        ]);
      });
  }, []);

  const fetchCustomers = useCallback(async () => {
    const res = await axios.get(API_BASE_URL + "/api/customers");
    setCustomers(res.data);

    try {
      const complaintsRes = await axios.get(API_BASE_URL + "/api/complaints");
      const complaintCount = Array.isArray(complaintsRes.data) ? complaintsRes.data.length : 0;
      setComplaintCount(complaintCount);
      setForm((current) => ({
        ...current,
        complaintId: current.revisitOf
          ? current.complaintId
          : buildComplaintIdPreview(complaintCount)
      }));
    } catch (error) {
      setForm((current) => ({
        ...current,
        complaintId: current.revisitOf
          ? current.complaintId
          : buildComplaintIdPreview(0)
      }));
    }
  }, []);

  const fetchEngineers = useCallback(async () => {
    const res = await axios.get(API_BASE_URL + "/api/engineers");
    setEngineers(res.data);
  }, []);

  const formatStoredDateForInput = useCallback((value) => {
    const normalized = String(value || "").trim();
    const match = normalized.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (match) {
      return `${match[3]}-${match[2]}-${match[1]}`;
    }
    return normalized.match(/^\d{4}-\d{2}-\d{2}$/) ? normalized : "";
  }, []);

  useEffect(() => {
    fetchCustomers();
    if (!isEngineerMode) {
      fetchEngineers();
    } else if (engineerLookup) {
      setForm((prev) => ({
        ...prev,
        type: "complaint",
        engineerAssigned: engineerLookup
      }));
    }
  }, [engineerLookup, fetchCustomers, fetchEngineers, isEngineerMode]);

  useEffect(() => {
    if (!isEditMode) {
      return;
    }

    if (!canEditComplaint) {
      navigate(isAccountsMode ? "/accounts/complaints" : "/complaints", { replace: true });
      return;
    }

    let isMounted = true;

    const fetchRecord = async () => {
      try {
        setLoadingComplaint(true);
        const response = isServiceEditMode
          ? await axios.get(API_BASE_URL + `/api/general-services/${serviceId}`)
          : await axios.get(API_BASE_URL + `/api/complaints/${id}`);
        const record = response.data;
        if (!isMounted || !record) {
          return;
        }

        const matchedCustomer = customers.find((customer) => String(customer._id) === String(record.customerId));
        const matchedEngineer = engineers.find((engineer) =>
          [engineer.engineerId, engineer.email, engineer.name]
            .filter(Boolean)
            .includes(record.engineerAssignedId || record.engineerAssignedEmail || record.engineerAssigned)
        );

        setSelectedCustomer(
          matchedCustomer
            ? {
                value: matchedCustomer._id,
                label: matchedCustomer.name,
                data: matchedCustomer
              }
            : null
        );
        setSelectedEngineer(isServiceEditMode
          ? null
          : matchedEngineer
            ? {
                value: matchedEngineer.engineerId || matchedEngineer.email || matchedEngineer.name,
                label: matchedEngineer.engineerId ? `${matchedEngineer.name} (${matchedEngineer.engineerId})` : matchedEngineer.name,
                data: matchedEngineer
              }
            : null);
        setVisitDateInput(formatStoredDateForInput(record.visitDate));
        setServiceDateInput(formatStoredDateForInput(record.serviceDate));
        setNextServiceDateInput(formatStoredDateForInput(record.nextServiceDate));

        const isAbbRecord = Boolean(record.isAbbWarranty) || record.productType === "ABB Warranty" || String(record.siteAdvice || "").includes("ABB Warranty");

        setForm((prev) => ({
          ...prev,
          type: isServiceEditMode ? "service" : isAbbRecord ? "abb_warranty" : "complaint",
          isAbbWarranty: isAbbRecord,
          complaintId: record.complaintId || prev.complaintId,
          serviceId: record.serviceId || prev.serviceId,
          customerId: record.customerId || (isAbbRecord ? "ABB-WARRANTY" : ""),
          productType: record.productType || (isAbbRecord ? "ABB Warranty" : ""),
          nature: record.issue || record.complaint || "",
          siteAdvice: record.siteAdvice || "",
          sharePreviousHistory: Boolean(record.sharePreviousHistory),
          engineerAssigned: record.engineerAssignedId || record.engineerAssignedEmail || record.engineerAssigned || "",
          visitDate: record.visitDate || "",
          serviceDate: record.serviceDate || "",
          nextServiceDate: record.nextServiceDate || "",
          stickerRequired: isServiceEditMode ? true : Boolean(record.stickerRequired),
          assignedEngineers: Array.isArray(record.assignedEngineers) ? record.assignedEngineers : [],
          company: record.company || record.customerName || "",
          issue: record.issue || record.complaint || "",
          contactName: record.contactName || "",
          contactPhone: record.contactPhone || record.phone || "",
          contactEmail: record.contactEmail || "",
          address:
            formatAddress(getWorkAddress(matchedCustomer || {})) ||
            record.address ||
            ""
        }));
      } catch (error) {
        alert(error.response?.data?.message || `Failed to load ${isServiceEditMode ? "service" : "complaint"}`);
        navigate(isAccountsMode ? "/accounts/complaints" : "/complaints", { replace: true });
      } finally {
        if (isMounted) {
          setLoadingComplaint(false);
        }
      }
    };

    fetchRecord();

    return () => {
      isMounted = false;
    };
  }, [canEditComplaint, customers, engineers, formatStoredDateForInput, id, isAccountsMode, isEditMode, isServiceEditMode, navigate, serviceId]);

  useEffect(() => {
    if (location.state?.prefill) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state?.prefill, navigate]);

  useEffect(() => {
    if (isEngineerMode || !initialEngineerAssignedRef.current || engineers.length === 0) {
      return;
    }

    const matchedEngineer = engineers.find((engineer) =>
      [engineer.engineerId, engineer.email, engineer.name]
        .filter(Boolean)
        .includes(initialEngineerAssignedRef.current)
    );

    if (!matchedEngineer) {
      return;
    }

    setSelectedEngineer({
      value: matchedEngineer._id,
      label: matchedEngineer.name,
      data: matchedEngineer
    });
  }, [engineers, isEngineerMode]);

  const handleFormattedDateChange = (dateVal, setter, fieldName) => {
    setter(dateVal);
    if (!dateVal) {
      setForm((current) => ({ ...current, [fieldName]: "" }));
      return;
    }
    const [y, m, d] = dateVal.split("-");
    setForm((current) => ({ ...current, [fieldName]: `${d}/${m}/${y}` }));
  };

  const handleDateChange = (e) => {
    const dateVal = e.target.value;
    handleFormattedDateChange(dateVal, setVisitDateInput, "visitDate");
  };

  const handleServiceDateChange = (e) => {
    const dateVal = e.target.value;
    handleFormattedDateChange(dateVal, setServiceDateInput, "serviceDate");
    // Auto-compute Due Date = one year from Service Date minus one day (e.g. 20-10-2026 -> 19-10-2027)
    if (dateVal) {
      const parts = dateVal.split("-").map(Number);
      if (parts.length === 3) {
        const [y, m, d] = parts;
        const due = new Date(y, m - 1, d);
        due.setFullYear(due.getFullYear() + 1);
        due.setDate(due.getDate() - 1);
        const dy = due.getFullYear();
        const dm = String(due.getMonth() + 1).padStart(2, "0");
        const dd = String(due.getDate()).padStart(2, "0");
        const dueInputVal = `${dy}-${dm}-${dd}`;
        setNextServiceDateInput(dueInputVal);
        setForm((current) => ({ ...current, nextServiceDate: `${dd}/${dm}/${dy}` }));
      }
    } else {
      setNextServiceDateInput("");
      setForm((current) => ({ ...current, nextServiceDate: "" }));
    }
  };

  const handleNextServiceDateChange = (e) => {
    handleFormattedDateChange(e.target.value, setNextServiceDateInput, "nextServiceDate");
  };

  const addAssignedEngineer = () => {
    setForm((current) => ({
      ...current,
      assignedEngineers: [
        ...(Array.isArray(current.assignedEngineers) ? current.assignedEngineers : []),
        { engineerId: "", engineerName: "", engineerEmail: "", team: "" }
      ]
    }));
  };

  const updateAssignedEngineer = (index, fieldName, value) => {
    setForm((current) => ({
      ...current,
      assignedEngineers: (Array.isArray(current.assignedEngineers) ? current.assignedEngineers : []).map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }

        if (fieldName === "engineerKey") {
          const matchedEngineer = engineers.find((engineer) =>
            [engineer.engineerId, engineer.email, engineer.name]
              .filter(Boolean)
              .includes(value)
          );

          return matchedEngineer
            ? {
                ...item,
                engineerId: matchedEngineer.engineerId || "",
                engineerName: matchedEngineer.name || "",
                engineerEmail: matchedEngineer.email || ""
              }
            : item;
        }

        return { ...item, [fieldName]: value };
      })
    }));
  };

  const removeAssignedEngineer = (index) => {
    setForm((current) => ({
      ...current,
      assignedEngineers: (Array.isArray(current.assignedEngineers) ? current.assignedEngineers : []).filter((_, itemIndex) => itemIndex !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (saving || submitLockRef.current) {
      return;
    }

    try {
      submitLockRef.current = true;
      setSaving(true);
      
      const isServiceTicket = isServiceEditMode || (form.type === "service" && form.serviceDate && form.nextServiceDate);
      if (isServiceTicket) {
        let assignedList = Array.isArray(form.assignedEngineers) ? [...form.assignedEngineers] : [];
        if (assignedList.length === 0 && selectedEngineer) {
          assignedList = [{
            engineerId: selectedEngineer.data?.engineerId || "",
            engineerName: selectedEngineer.data?.name || "",
            engineerEmail: selectedEngineer.data?.email || "",
            team: ""
          }];
        }

        const payload = {
          customerId: form.customerId,
          serviceName: "General Service",
          customerName: selectedCustomer?.data?.name || form.company,
          company: selectedCustomer?.data?.name || form.company,
          address: form.address,
          city: selectedCustomer?.data?.address?.city || selectedCustomer?.data?.billingAddress?.city || "",
          contactName: form.contactName,
          phone: form.contactPhone,
          email: selectedCustomer?.data?.email || "",
          serviceDate: form.serviceDate,
          nextServiceDate: form.nextServiceDate,
          stickerRequired: Boolean(form.stickerRequired),
          complaint: form.nature,
          notes: form.siteAdvice,
          assignedEngineers: assignedList
        };

        if (!payload.serviceDate) {
          throw new Error("Service Date is required");
        }

        if (!payload.nextServiceDate) {
          throw new Error("Due Date is required");
        }

        if (payload.assignedEngineers.length === 0) {
          throw new Error("Add at least one engineer for service");
        }

        if (isServiceEditMode) {
          const response = await axios.put(API_BASE_URL + `/api/general-services/${serviceId}`, payload);
          setForm((current) => ({
            ...current,
            serviceId: response.data?.serviceId || current.serviceId
          }));
          alert("Service updated successfully");
        } else {
          const response = await axios.post(API_BASE_URL + "/api/general-services", payload);
          setForm((current) => ({
            ...current,
            serviceId: response.data?.serviceId || current.serviceId
          }));
          alert("Service created successfully");
        }
      } else {
        if (id) {
          await axios.put(API_BASE_URL + `/api/complaints/${id}`, form);
          alert("Complaint updated successfully");
        } else {
          const response = await axios.post(API_BASE_URL + "/api/complaints", {
            ...form,
            revisitOf: revisitPrefill?.revisitOf || form.revisitOf || "",
            originalComplaintId: revisitPrefill?.originalComplaintId || form.originalComplaintId || ""
          });
          alert("Complaint Created Successfully");
          setForm((current) => ({
            ...current,
            complaintId: response.data?.complaintId || current.complaintId
          }));
        }
      }
      navigate(isEngineerMode ? "/engineer-complaints" : isAccountsMode ? "/accounts/complaints" : "/complaints");
    } catch (error) {
      console.error(error);
      const isServiceTicket = isServiceEditMode || (form.type === "service" && form.serviceDate && form.nextServiceDate);
      const fallbackMessage = isServiceTicket
        ? (isServiceEditMode ? "Error updating service" : "Error creating service")
        : (id ? "Error updating complaint" : "Error creating complaint");
      alert(error.response?.data?.message || error.message || fallbackMessage);
    } finally {
      setSaving(false);
      submitLockRef.current = false;
    }
  };

  // Options
  const customerOptions = useMemo(() => {
    return customers.map((c) => {
      const nameStr = c.name || "";
      const idStr = c.customerId || "";
      const label = idStr ? `${nameStr} (${idStr})` : nameStr;
      return {
        value: c._id,
        label: label || "Unnamed Customer",
        data: c
      };
    });
  }, [customers]);

  const engineerOptions = useMemo(() => {
    return engineers.map((e) => ({
      value: e.engineerId || e.email || e.name,
      label: e.engineerId ? `${e.name} (${e.engineerId})` : e.name,
      data: e
    }));
  }, [engineers]);

  const contactOptions = useMemo(() => {
    if (!selectedCustomer || !selectedCustomer.data) {
      return [];
    }
    const cust = selectedCustomer.data;
    const list = [];

    // Add primary contact if name or phone is present
    if (cust.contactPerson || cust.phone) {
      list.push({
        label: `${cust.contactPerson || "Primary Contact"} (${cust.phone || "No Phone"}) - Primary`,
        name: cust.contactPerson || "",
        phone: cust.phone || ""
      });
    }

    // Add additional contacts
    if (Array.isArray(cust.additionalContacts)) {
      cust.additionalContacts.forEach((c) => {
        if (c.name || c.mobileNumber) {
          list.push({
            label: `${c.name || "Unnamed"} (${c.mobileNumber || "No Phone"}) - ${c.role || "Other"}`,
            name: c.name || "",
            phone: c.mobileNumber || ""
          });
        }
      });
    }

    return list;
  }, [selectedCustomer]);



  const formatAddress = (address) => {
    if (!address) return "-";

    if (typeof address === "string") {
      return address;
    }

    return [
      address.street,
      address.area,
      address.city,
      address.state,
      address.pincode
    ]
      .filter(Boolean)
      .join(", ");
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "44px",
      borderRadius: "0.75rem",
      borderColor: state.isFocused ? "#2563eb" : "#cbd5e1",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(37, 99, 235, 0.14)" : "none",
      fontSize: "16px",
      color: "#0f172a",
      "&:hover": {
        borderColor: "#2563eb"
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: "#64748b",
      fontSize: "16px"
    }),
    input: (base) => ({
      ...base,
      color: "#0f172a",
      fontSize: "16px"
    }),
    singleValue: (base) => ({
      ...base,
      color: "#0f172a",
      fontSize: "16px"
    }),
    menu: (base) => ({
      ...base,
      zIndex: 60,
      borderRadius: "1rem",
      overflow: "hidden",
      boxShadow: "0 20px 45px rgba(15, 23, 42, 0.14)"
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 60
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#dbeafe"
        : state.isFocused
          ? "#eff6ff"
          : "#ffffff",
      color: "#0f172a",
      fontSize: "16px"
    }),
    indicatorSeparator: () => ({
      display: "none"
    })
  };





  const content = (
    <div className="min-h-screen bg-slate-50 pb-32">
      {/* Header bar within the page body */}
      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4 shadow-sm sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="material-symbols-outlined p-2 rounded-full hover:bg-slate-100 transition-colors text-blue-600 active:scale-95 duration-100"
          >
            arrow_back
          </button>
          <h1 className="text-lg md:text-xl font-bold text-slate-900">
            {isServiceEditMode || form.type === "service" ? `Service ID: ${form.serviceId}` : `Ticket ID: ${form.complaintId}`}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold bg-blue-100 text-blue-800 px-3 py-1 rounded-full uppercase tracking-wider">
            {isEditMode ? "EDITING" : "DRAFTING"}
          </span>
        </div>
      </div>

      {loadingComplaint && (
        <div className="mx-auto max-w-6xl px-6 pt-6">
          <div className="rounded-2xl border border-blue-100 bg-white px-4 py-3 text-sm font-medium text-slate-600 shadow-sm">
            Loading ticket details...
          </div>
        </div>
      )}

      {/* Main Bento Grid Layout */}
      <form onSubmit={handleSubmit} className="mx-auto max-w-6xl px-6 py-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          
          {/* Ticket Identifier Card & Type Switcher (Wide) */}
          <section className="md:col-span-12 bg-white border border-slate-200 rounded-xl p-6 shadow-sm relative overflow-hidden group">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  {form.type === "service" ? "Service Identifier" : form.type === "abb_warranty" || form.isAbbWarranty ? "ABB Warranty Ticket" : "Complaint Identifier"}
                </span>
                <h2 className="text-2xl font-extrabold text-blue-600 mt-1 font-mono">
                  {isServiceEditMode || form.type === "service" ? form.serviceId : form.complaintId}
                </h2>
              </div>

              {!isEditMode && (
                <div className="flex flex-wrap items-center gap-2 p-1.5 bg-slate-100 rounded-xl border border-slate-200">
                  <button
                    type="button"
                    onClick={() => {
                      setForm((prev) => ({
                        ...prev,
                        type: "complaint",
                        isAbbWarranty: false,
                        productType: prev.productType === "ABB Warranty" ? "" : prev.productType,
                        complaintId: prev.revisitOf ? prev.complaintId : buildComplaintIdPreview(complaintCount, new Date(), "000")
                      }));
                    }}
                    className={`px-3.5 py-2 rounded-lg font-bold text-xs sm:text-sm transition-all flex items-center gap-1.5 ${
                      !form.isAbbWarranty && form.type !== "abb_warranty"
                        ? "bg-blue-600 text-white shadow-sm"
                        : "text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    <span className="material-symbols-outlined text-base">build_circle</span>
                    <span>Standard / General Service</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setForm((prev) => ({
                        ...prev,
                        type: "abb_warranty",
                        isAbbWarranty: true,
                        serviceDate: "",
                        nextServiceDate: "",
                        productType: "ABB Warranty",
                        customerId: "ABB-WARRANTY",
                        complaintId: prev.revisitOf ? prev.complaintId : buildComplaintIdPreview(complaintCount, new Date(), "ABB")
                      }));
                      setServiceDateInput("");
                      setNextServiceDateInput("");
                    }}
                    className={`px-3.5 py-2 rounded-lg font-bold text-xs sm:text-sm transition-all flex items-center gap-1.5 ${
                      form.type === "abb_warranty" || form.isAbbWarranty
                        ? "bg-teal-600 text-white shadow-sm"
                        : "text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    <span className="material-symbols-outlined text-base">verified</span>
                    <span>ABB Warranty Complaint</span>
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* Customer Section */}
          {form.type === "abb_warranty" || form.isAbbWarranty ? (
            /* ABB Warranty: Direct Customer Details (No Customer Search Required) */
            <section className="md:col-span-7 bg-white border border-teal-200 rounded-xl p-6 space-y-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-teal-600">verified</span>
                  <h3 className="text-lg font-bold text-slate-900">Customer &amp; Site Details</h3>
                </div>
                <span className="text-xs bg-teal-100 text-teal-800 font-bold px-2.5 py-1 rounded-full">
                  ABB Warranty — Direct Entry
                </span>
              </div>

              <div className="space-y-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-bold text-slate-800">
                    Customer / Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. ABC Manufacturing Pvt Ltd"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    required
                    className="w-full h-12 px-4 bg-slate-100 border-none rounded-lg focus:ring-2 focus:ring-teal-600 focus:bg-white transition-all text-base outline-none"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-bold text-slate-800">
                      ABB Warranty Ticket Number
                    </label>
                    <span className="text-xs font-mono text-teal-700 bg-teal-50 px-2 py-0.5 rounded border border-teal-200">
                      Main Ticket: {form.complaintId}
                    </span>
                  </div>
                  <input
                    type="text"
                    placeholder="e.g. 123 / ABB-123 / C926/ABB-123"
                    value={form.abbTicketNo || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      const cleanNum = val.replace(/^C\d+\/ABB-?/i, "").replace(/^ABB-?/i, "").trim();
                      const today = new Date();
                      const yr = String(today.getFullYear()).slice(-2);
                      const m = String(today.getMonth() + 1);
                      const monthYear = `${m}${yr}`;
                      const formattedId = val.trim()
                        ? `C${monthYear}/ABB-${cleanNum || val.trim()}`
                        : buildComplaintIdPreview(complaintCount, new Date(), "ABB");

                      setForm((prev) => ({
                        ...prev,
                        abbTicketNo: val,
                        complaintId: prev.revisitOf ? prev.complaintId : formattedId
                      }));
                    }}
                    className="w-full h-12 px-4 bg-slate-100 border-none rounded-lg focus:ring-2 focus:ring-teal-600 focus:bg-white transition-all text-base outline-none font-medium"
                  />
                  <span className="text-xs text-slate-500">
                    Entering a ticket number will set your official main ticket ID as <strong className="text-teal-800">{form.complaintId}</strong>.
                  </span>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-bold text-slate-800">
                    Site / Work Address
                  </label>
                  <textarea
                    rows="2"
                    placeholder="Enter site / plant address, city, location..."
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    className="w-full p-3 bg-slate-100 border-none rounded-lg focus:ring-2 focus:ring-teal-600 focus:bg-white transition-all text-base resize-none outline-none"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-bold text-slate-800">Contact Person</label>
                    <input
                      type="text"
                      placeholder="Contact person name"
                      value={form.contactName}
                      onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                      className="w-full h-12 px-4 bg-slate-100 border-none rounded-lg focus:ring-2 focus:ring-teal-600 focus:bg-white transition-all text-base outline-none"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-bold text-slate-800">Contact Phone</label>
                    <input
                      type="tel"
                      placeholder="+91 0000000000"
                      value={form.contactPhone}
                      onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                      className="w-full h-12 px-4 bg-slate-100 border-none rounded-lg focus:ring-2 focus:ring-teal-600 focus:bg-white transition-all text-base outline-none"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-bold text-slate-800">Contact Email (Optional)</label>
                  <input
                    type="email"
                    placeholder="customer@example.com"
                    value={form.contactEmail || ""}
                    onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                    className="w-full h-12 px-4 bg-slate-100 border-none rounded-lg focus:ring-2 focus:ring-teal-600 focus:bg-white transition-all text-base outline-none"
                  />
                </div>
              </div>
            </section>
          ) : (
            /* Standard / Service: Select Existing Customer */
            <section className="md:col-span-7 bg-white border border-slate-200 rounded-xl p-6 space-y-4 shadow-sm">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-blue-600">person_search</span>
                <h3 className="text-lg font-bold text-slate-900">Select Customer</h3>
              </div>
              
              <div className="space-y-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-bold text-slate-800">Search Customer</label>
                  <Select
                    options={customerOptions}
                    value={selectedCustomer}
                    styles={selectStyles}
                    onChange={(option) => {
                      setSelectedCustomer(option);
                      if (option) {
                        const customerIdStr = option.data?.customerId || "";
                        const numPart = customerIdStr.replace("CU-", "") || "000";
                        const parsedNum = parseInt(numPart, 10) || 0;
                        const customerCode = String(parsedNum).padStart(3, "0");
                        setForm({
                          ...form,
                          customerId: option.value,
                          company: option.data?.name || "",
                          contactName: option.data?.contactPerson || "",
                          contactPhone: option.data?.phone || "",
                          address: formatAddress(getWorkAddress(option.data)),
                          complaintId: form.revisitOf ? form.complaintId : buildComplaintIdPreview(complaintCount, new Date(), customerCode)
                        });
                      } else {
                        setForm({
                          ...form,
                          customerId: "",
                          company: "",
                          contactName: "",
                          contactPhone: "",
                          address: "",
                          complaintId: form.revisitOf ? form.complaintId : buildComplaintIdPreview(complaintCount, new Date(), "000")
                        });
                      }
                    }}
                    placeholder="Search customer name or ID..."
                  />
                </div>

                {selectedCustomer && contactOptions.length > 0 && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-bold text-slate-800">Select Contact Person</label>
                    <div className="relative">
                      <select
                        value=""
                        onChange={(e) => {
                          const idx = parseInt(e.target.value, 10);
                          if (!isNaN(idx) && contactOptions[idx]) {
                            const contact = contactOptions[idx];
                            setForm((prev) => ({
                              ...prev,
                              contactName: contact.name,
                              contactPhone: contact.phone
                            }));
                          }
                        }}
                        className="w-full h-12 px-4 bg-slate-100 border-none rounded-lg focus:ring-2 focus:ring-blue-600 focus:bg-white transition-all appearance-none text-base outline-none"
                      >
                        <option value="">-- Select Contact to Auto-Fill --</option>
                        {contactOptions.map((opt, index) => (
                          <option key={index} value={index}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <span className="material-symbols-outlined absolute right-4 top-3 pointer-events-none text-slate-400">expand_more</span>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-bold text-slate-800">Contact Name</label>
                    <input
                      type="text"
                      placeholder="Full name"
                      value={form.contactName}
                      onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                      className="w-full h-12 px-4 bg-slate-100 border-none rounded-lg focus:ring-2 focus:ring-blue-600 focus:bg-white transition-all text-base outline-none"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-bold text-slate-800">Contact Phone</label>
                    <input
                      type="tel"
                      placeholder="+91 000000000"
                      value={form.contactPhone}
                      onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                      className="w-full h-12 px-4 bg-slate-100 border-none rounded-lg focus:ring-2 focus:ring-blue-600 focus:bg-white transition-all text-base outline-none"
                    />
                  </div>
                </div>

                {selectedCustomer && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
                    <label className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
                      Customer Contact Info
                    </label>
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200">
                        <p className="font-semibold text-slate-500">Email:</p>
                        <p className="mt-1 break-words font-medium text-slate-900">{selectedCustomer.data.email}</p>
                      </div>
                      <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200">
                        <p className="font-semibold text-slate-500">Phone:</p>
                        <p className="mt-1 font-medium text-slate-900">{formatPhoneDisplay(selectedCustomer.data.phone)}</p>
                      </div>
                      <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200 sm:col-span-3">
                        <p className="font-semibold text-slate-500">Work Address:</p>
                        <p className="mt-1 font-medium text-slate-900">{formatAddress(getWorkAddress(selectedCustomer.data))}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Product & Dates Section */}
          <section className="md:col-span-5 bg-white border border-slate-200 rounded-xl p-6 space-y-4 shadow-sm">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-blue-600">calendar_month</span>
              <h3 className="text-lg font-bold text-slate-900">Product &amp; Dates</h3>
            </div>
            
            <div className="space-y-4">
              {form.type === "abb_warranty" || form.isAbbWarranty ? (
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-bold text-slate-800">Product Category</label>
                  <div className="w-full h-12 px-4 bg-teal-50 border border-teal-200 rounded-lg flex items-center justify-between text-teal-900 font-bold text-base">
                    <span>ABB Warranty</span>
                    <span className="text-xs bg-teal-200 text-teal-800 px-2 py-0.5 rounded-full font-bold">Standard</span>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-bold text-slate-800">Product Type</label>
                  <div className="relative">
                    <select
                      value={form.productType || ""}
                      onChange={(e) => setForm({ ...form, productType: e.target.value })}
                      className="w-full h-12 px-4 bg-slate-100 border-none rounded-lg focus:ring-2 focus:ring-blue-600 focus:bg-white transition-all appearance-none text-base outline-none"
                    >
                      <option value="" disabled>Product type</option>
                      <option value="ABB Warranty">ABB Warranty</option>
                      <option value="Industrial Generator">Industrial Generator</option>
                      <option value="Power Transformer">Power Transformer</option>
                      <option value="Switchgear Unit">Switchgear Unit</option>
                      <option value="breaker">Breaker</option>
                      <option value="acb">ACB</option>
                      <option value="mccb">MCCB</option>
                      <option value="vcb">VCB</option>
                      <option value="panel">Panel</option>
                      <option value="other">Other</option>
                    </select>
                    <span className="material-symbols-outlined absolute right-4 top-3 pointer-events-none text-slate-400">expand_more</span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-bold text-slate-800">Visit Date</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={visitDateInput}
                      onChange={handleDateChange}
                      onClick={(e) => e.target.showPicker?.()}
                      className="w-full h-12 px-4 bg-slate-100 border-none rounded-lg focus:ring-2 focus:ring-blue-600 focus:bg-white transition-all text-base outline-none"
                    />
                    <span className="material-symbols-outlined absolute right-4 top-3 pointer-events-none text-slate-400">calendar_today</span>
                  </div>
                </div>

                {!(form.type === "abb_warranty" || form.isAbbWarranty) && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-bold text-slate-800">
                        Service Date <span className="font-normal opacity-60">(opt)</span>
                      </label>
                      <input
                        type="date"
                        value={serviceDateInput}
                        onChange={handleServiceDateChange}
                        onClick={(e) => e.target.showPicker?.()}
                        className="w-full h-12 px-4 bg-slate-100 border-none rounded-lg focus:ring-2 focus:ring-blue-600 focus:bg-white transition-all text-base outline-none"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-bold text-slate-800">
                        Due Date <span className="font-normal opacity-60">(opt)</span>
                      </label>
                      <input
                        type="date"
                        value={nextServiceDateInput}
                        onChange={handleNextServiceDateChange}
                        onClick={(e) => e.target.showPicker?.()}
                        className="w-full h-12 px-4 bg-slate-100 border-none rounded-lg focus:ring-2 focus:ring-blue-600 focus:bg-white transition-all text-base outline-none"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Assign Engineer Section */}
          <section className="md:col-span-12 bg-white border border-slate-200 rounded-xl p-6 space-y-4 shadow-sm">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-blue-600">engineering</span>
                <h3 className="text-lg font-bold text-slate-900">Assign Engineer</h3>
              </div>
              <button
                type="button"
                onClick={addAssignedEngineer}
                className="bg-slate-900 text-white px-6 py-2 rounded-lg font-semibold hover:opacity-90 active:scale-95 transition-all flex items-center gap-1.5 text-sm"
              >
                <span className="material-symbols-outlined text-sm">add</span>
                <span>Add Engineer</span>
              </button>
            </div>

            <div className="space-y-4">
              {isEngineerMode ? (
                <input
                  type="text"
                  value={engineerName}
                  readOnly
                  className="w-full h-12 px-4 bg-slate-100 border-none rounded-lg outline-none text-base"
                />
              ) : (
                <Select
                  options={engineerOptions}
                  value={selectedEngineer}
                  styles={selectStyles}
                  onChange={(option) => {
                    setSelectedEngineer(option);
                    setForm({
                      ...form,
                      engineerAssigned: option?.data?.engineerId || option?.data?.email || option?.data?.name || ""
                    });
                  }}
                  placeholder="Search and select engineer..."
                />
              )}

              {Array.isArray(form.assignedEngineers) && form.assignedEngineers.length > 0 ? (
                <div className="space-y-3">
                  {form.assignedEngineers.map((assignment, index) => (
                    <div key={`${assignment.engineerId || "engineer"}-${index}`} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <div className="grid gap-3 md:grid-cols-[1.5fr_1fr_auto]">
                        <select
                          value={assignment.engineerId || assignment.engineerEmail || assignment.engineerName || ""}
                          onChange={(e) => updateAssignedEngineer(index, "engineerKey", e.target.value)}
                          className="w-full h-12 px-4 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-600 transition-all text-base outline-none"
                        >
                          <option value="">Select engineer</option>
                          {engineers.map((engineer) => {
                            const optionValue = engineer.engineerId || engineer.email || engineer.name;
                            return (
                              <option key={engineer._id} value={optionValue}>
                                {engineer.engineerId ? `${engineer.name} (${engineer.engineerId})` : engineer.name}
                              </option>
                            );
                          })}
                        </select>
                        <select
                          value={assignment.team || ""}
                          onChange={(e) => updateAssignedEngineer(index, "team", e.target.value)}
                          className="w-full h-12 px-4 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-600 transition-all text-base outline-none"
                        >
                          <option value="">No Team</option>
                          {dynamicTeams.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => removeAssignedEngineer(index)}
                          className="rounded-lg border border-red-200 px-4 py-3 text-sm font-semibold text-red-600 hover:bg-red-50 transition-all"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border border-dashed border-slate-300 rounded-xl p-6 flex flex-col items-center justify-center text-center space-y-1 bg-slate-50">
                  <span className="material-symbols-outlined text-4xl text-slate-400">person_off</span>
                  <p className="text-sm font-medium text-slate-600 italic">No engineers added yet.</p>
                  <p className="text-xs text-slate-400">Add one or more engineers and optionally group them into teams.</p>
                </div>
              )}

              <div className="flex items-center gap-3 p-3 bg-slate-100 rounded-lg">
                <input
                  id="sticker_option"
                  type="checkbox"
                  checked={Boolean(form.stickerRequired)}
                  onChange={(e) => setForm((current) => ({ ...current, stickerRequired: e.target.checked }))}
                  className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="sticker_option" className="text-sm font-medium text-slate-900 cursor-pointer">
                  Show sticker option on ticket
                </label>
              </div>
            </div>
          </section>

          {/* Description Section */}
          <section className="md:col-span-12 bg-white border border-slate-200 rounded-xl p-6 space-y-4 shadow-sm">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-blue-600">description</span>
              <h3 className="text-lg font-bold text-slate-900">Description (Problem / Service Details)</h3>
            </div>

            <div className="space-y-4">
              <div className="flex flex-col gap-1.5">
                <textarea
                  value={form.nature}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      nature: e.target.value,
                      issue: e.target.value
                    })
                  }
                  placeholder="Describe the issue or service details in detail..."
                  rows="6"
                  maxLength={1000}
                  className="w-full p-4 bg-slate-100 border-none rounded-lg focus:ring-2 focus:ring-blue-600 focus:bg-white transition-all text-base resize-none outline-none"
                />
                <div className="flex justify-end">
                  <span className={`text-xs font-semibold ${form.nature.length > 1000 ? "text-red-600" : "text-slate-400"}`}>
                    {form.nature.length}/1000
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-bold text-slate-800">Notes / Site Advice</label>
                <textarea
                  value={form.siteAdvice}
                  onChange={(e) => setForm({ ...form, siteAdvice: e.target.value })}
                  placeholder="Add any instructions, site advice, or service notes..."
                  rows="3"
                  className="w-full p-4 bg-slate-100 border-none rounded-lg focus:ring-2 focus:ring-blue-600 focus:bg-white transition-all text-base resize-none outline-none"
                />
              </div>
            </div>
          </section>

        </div>

        {/* Sticky Bottom NavBar */}
        <div className="fixed bottom-0 left-0 w-full z-40 flex justify-center items-center px-6 py-4 pb-safe bg-white/80 backdrop-blur-md border-t border-slate-200 shadow-lg">
          <button
            type="submit"
            disabled={saving}
            className="max-w-2xl w-full h-14 bg-blue-600 text-white rounded-xl font-semibold text-lg shadow-lg hover:opacity-90 active:scale-95 transition-all flex items-center justify-center gap-2 disabled:bg-blue-300 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined fill-current">save</span>
            {saving
              ? "SAVING..."
              : form.type === "service"
                ? isServiceEditMode
                  ? "UPDATE SERVICE"
                  : "SAVE SERVICE"
                : form.type === "abb_warranty" || form.isAbbWarranty
                  ? id
                    ? "UPDATE ABB WARRANTY COMPLAINT"
                    : "SAVE ABB WARRANTY COMPLAINT"
                  : id
                    ? "UPDATE COMPLAINT"
                    : "SAVE COMPLAINT"}
          </button>
        </div>
      </form>
    </div>
  );

  if (isEngineerMode) {
    return <EngineerLayout>{content}</EngineerLayout>;
  }

  return content;
}

export default CreateComplaint;


